import { inheritsLoose as _inheritsLoose } from './_virtual/_rollupPluginBabelHelpers.js';
import { debugAssert } from './assert.js';
import { UPDATE_ERROR, isMatchingSubscription, EFFECT_ERROR } from './model.js';
import { EventEmitter } from './eventemitter.js';

var ANONYMOUS_SOURCE = "$";
var BROADCAST_SOURCE = "@";
var REPLY_SOURCE = "<";

var Supervisor = /*#__PURE__*/function (_EventEmitter) {
  _inheritsLoose(Supervisor, _EventEmitter);

  // Explicit constructor results in shorter minified code
  function Supervisor() {
    var _this;

    _this = _EventEmitter.call(this) || this;
    _this._nested = {};
    return _this;
  }
  /**
   * Returns the nested State for the given model and name if it
   * exists, name defaults to model id.
   */


  var _proto = Supervisor.prototype;

  _proto.getState = function getState(m, name) {
    if (process.env.NODE_ENV !== "production") {
      ensureModel(this._getStorage(), m);
    }

    var inst = this._nested[name || m.id];

    if (inst) {
      // TODO: Use path
      debugAssert(inst._name === (name || m.id), "State name '" + inst._name + "' does not match key name '" + (name || m.id));
    }

    return inst;
  }
  /**
   * Attempts to retrieve the nested State for the given model and name,
   * if it does not exist it will be created, name defaults to model id.
   */
  ;

  _proto.createState = function createState(m, params, name) {
    var id = m.id,
        init = m.init;

    if (!name) {
      name = id;
    }

    if (this._nested[name]) {
      // TODO: Maybe add path?
      throw new Error("Duplicate state '" + name + "'");
    }

    var storage = this._getStorage();

    tryAddModel(storage, m);

    var _init = init(params),
        data = _init.data,
        messages = _init.messages;

    debugAssert(this instanceof Storage || this instanceof State, "this is not an instance of Storage or State");
    var instance = new State(id, this, data, name);
    var path = instance.getPath();
    this._nested[name] = instance;
    storage.emit("stateCreated", path, params, data);

    if (messages.length > 0) {
      instance._init = processEffects(storage, processInstanceMessages(storage, instance._supervisor, messages.map(function (m) {
        return createInflightMessage(storage, path, m);
      })));
    }

    return instance;
  };

  _proto.removeState = function removeState(m, name) {
    var inst = this.getState(m, name);

    if (inst) {
      delete this._nested[name || inst._name];

      this._getStorage().emit("stateRemoved", inst.getPath(), inst._data);
    }
  }
  /**
   * Sends the given message to any matching State or Effect in the
   * state-tree.
   */
  ;

  _proto.sendMessage = function sendMessage(msg, srcName) {
    if (srcName === void 0) {
      srcName = ANONYMOUS_SOURCE;
    }

    var storage = this._getStorage();

    var msgPath = this.getPath().concat([srcName]);
    return processEffects(storage, processInstanceMessages(storage, this, [createInflightMessage(storage, msgPath, msg)]));
  };

  return Supervisor;
}(EventEmitter);
/**
 * Base node in a state-tree, anchors all states and carries all data.
 */


var Storage = /*#__PURE__*/function (_Supervisor) {
  _inheritsLoose(Storage, _Supervisor);

  /**
   * Models, used for subscribers, updates and messages.
   */
  // Explicit constructor results in shorter minified code
  function Storage() {
    var _this2;

    _this2 = _Supervisor.call(this) || this;
    _this2._effects = [];
    _this2._running = new Map();
    _this2._defs = {};
    _this2._waiting = [];
    return _this2;
  }
  /**
   * Returns the Storage backing all state in this tree.
   */


  var _proto2 = Storage.prototype;

  _proto2._getStorage = function _getStorage() {
    return this;
  }
  /**
   * Returns the path to this state.
   */
  ;

  _proto2.getPath = function getPath() {
    return [];
  }
  /**
   * Adds the supplied model to the Storage so it can be used when
   * using restoreSnapshot(). Throws if a model with the same id already exist.
   */
  ;

  _proto2.addModel = function addModel(model) {
    if (!tryAddModel(this, model)) {
      throw new Error("Duplicate model '" + model.id + "'.");
    }
  }
  /**
   * Returns the model with the given id, if it exists.
   */
  ;

  _proto2.getModel = function getModel(id) {
    return this._defs[id];
  }
  /**
   * Sends a message to all state-instances currently reachable from this
   * Storage instance.
   */
  ;

  _proto2.broadcastMessage = function broadcastMessage(msg, sourceName) {
    if (sourceName === void 0) {
      sourceName = BROADCAST_SOURCE;
    }

    var inflight = createInflightMessage(this, [sourceName], msg);
    var handled = false;
    /**
     * Broadcasts msg to all state instances with a depth first algo.
     */

    function handleBroadcast(storage, path, nested) {
      var returning = []; // We trust that the user has not been poking around in globals

      /* eslint-disable-next-line guard-for-in */

      for (var key in nested) {
        var instance = nested[key];
        var nestedPath = path.concat([key]);
        returning.push.apply(returning, processMessages(storage, instance, nestedPath, [inflight].concat(handleBroadcast(storage, nestedPath, instance._nested))));
        var i = returning.indexOf(inflight);

        if (i === -1) {
          handled = true;
        } else {
          returning.splice(i, 1);
        }
      }

      return returning;
    }

    var messages = handleBroadcast(this, [], this._nested);

    if (!handled) {
      messages.unshift(inflight);
    }

    return processEffects(this, messages);
  }
  /**
   * Looks up the specified target state and if found the message is processed
   * in the state, and any resulting messages are passed upwards. The reply
   * is always passed to effects at the end.
   */
  ;

  _proto2.replyMessage = function replyMessage(msg, targetState, sourceName) {
    if (sourceName === void 0) {
      sourceName = REPLY_SOURCE;
    }

    var reply = createInflightMessage(this, targetState.concat(sourceName), msg);
    var instance = findState(this, targetState); // If we have an instance, only pass the specific reply message to it and
    // then propagate any resulting messages.

    var inflight = instance ? processInstanceMessages(this, instance._supervisor, processMessages(this, instance, instance.getPath(), [reply])) : [reply];
    return processEffects(this, inflight);
  };

  _proto2.addEffect = function addEffect(eff) {
    this._effects.push(eff);
  };

  _proto2.removeEffect = function removeEffect(eff) {
    var i = this._effects.indexOf(eff);

    if (i !== -1) {
      this._effects.splice(i, 1);
    }
  }
  /**
   * Creates a snapshot of the current state-tree. State-data will not be
   * copied.
   */
  ;

  _proto2.getSnapshot = function getSnapshot() {
    return createSnapshot(this);
  }
  /**
   * Attempts to restore a snapshot. Will throw if required models are missing.
   */
  ;

  _proto2.restoreSnapshot = function restoreSnapshot(snapshot) {
    this.emit("snapshotRestore", snapshot);

    _restoreSnapshot(this, this, snapshot);

    this.emit("snapshotRestored");
  };

  _proto2.wait = function wait() {
    var _this3 = this;

    if (this._running.size === 0) {
      return Promise.resolve();
    }

    return new Promise(function (resolve) {
      _this3._waiting.push(resolve);
    });
  };

  _proto2.runningEffects = function runningEffects() {
    return Array.from(this._running.values());
  };

  return Storage;
}(Supervisor);
/**
 * Object representing an instance of a Model.
 */

var State = /*#__PURE__*/function (_Supervisor2) {
  _inheritsLoose(State, _Supervisor2);

  /**
   * Matches the Storage _defs collection.
   */

  /**
   * Matches the key used in the supervisor's `_nested` collection.
   */
  function State(id, supervisor, data, name) {
    var _this4;

    _this4 = _Supervisor2.call(this) || this;
    _this4._id = id;
    _this4._name = name;
    _this4._supervisor = supervisor;
    _this4._data = data;
    return _this4;
  }
  /**
   * Returns the data contained in this State.
   */


  var _proto3 = State.prototype;

  _proto3.getData = function getData() {
    return this._data;
  }
  /**
   * Returns the path to this state.
   */
  ;

  _proto3.getPath = function getPath() {
    var path = [];
    var s = this;

    while (s instanceof State) {
      path.unshift(s._name);
      s = s._supervisor;
    }

    return path;
  }
  /**
   * Returns a promise resolving when the state has initialized and its effects
   * have settled. If they already have settled it will return a resolved
   * promise.
   */
  ;

  _proto3.waitInit = function waitInit() {
    if (this._init) {
      return this._init;
    }

    return Promise.resolve(undefined);
  }
  /**
   * Returns the Storage backing all state in this tree.
   */
  ;

  _proto3._getStorage = function _getStorage() {
    var s = this._supervisor;

    while (s instanceof State) {
      s = s._supervisor;
    }

    return s;
  };

  return State;
}(Supervisor);

function _restoreSnapshot(storage, supervisor, snapshot) {
  var newNested = {}; // We trust that the user has not been poking around in globals

  /* eslint-disable-next-line guard-for-in */

  for (var k in snapshot) {
    var _snapshot$k = snapshot[k],
        _id = _snapshot$k.id,
        data = _snapshot$k.data,
        nested = _snapshot$k.nested; // Ensure the model exists when we restore

    getModelById(storage, _id);
    var inst = new State(_id, supervisor, data, k);

    _restoreSnapshot(storage, inst, nested);

    newNested[k] = inst;
  }

  supervisor._nested = newNested;
}
function tryAddModel(storage, model) {
  var id = model.id;

  if (!storage._defs[id]) {
    storage._defs[id] = model;
    return true;
  }

  ensureModel(storage, model);
  return false;
}
function ensureModel(storage, model) {
  var id = model.id;

  if (storage._defs[id] && storage._defs[id] !== model) {
    throw new Error("Model mismatch for '" + id + "'.");
  }
}
function getModelById(storage, id) {
  var spec = storage._defs[id];

  if (!spec) {
    // TODO: Error type
    throw new Error("Missing model for state '" + id + "'.");
  }

  return spec;
}
function createInflightMessage(storage, source, message) {
  storage.emit("messageQueued", message, source);
  return {
    _message: message,
    _source: source,
    _received: false
  };
}
function findState(supervisor, path) {
  for (var _i2 = 0; _i2 < path.length; _i2++) {
    var p = path[_i2];

    if (!supervisor._nested[p]) {
      return null;
    }

    supervisor = supervisor._nested[p];
  }

  return supervisor instanceof State ? supervisor : null;
}
function processInstanceMessages(storage, instance, inflight) {
  var sourcePath = instance.getPath();

  while (instance instanceof State) {
    inflight = processMessages(storage, instance, sourcePath, inflight); // Traverse down one level

    sourcePath = sourcePath.slice(0, -1);
    instance = instance._supervisor;
  }

  return inflight;
}
function processMessages(storage, instance, sourcePath, inflight) {
  var newMessages = [];
  var definition = getModelById(storage, instance._id);
  var update = definition.update;

  for (var _i4 = 0; _i4 < inflight.length; _i4++) {
    var currentInflight = inflight[_i4];
    var _message = currentInflight._message;
    var _data = instance._data;
    var updateRequest = null;

    try {
      updateRequest = update(_data, _message);
    } catch (e) {
      storage.emit("updateError", e, _data, sourcePath, _message);
      instance.emit("updateError", e, _data, sourcePath, _message);
      newMessages.push(createInflightMessage(storage, sourcePath, {
        tag: UPDATE_ERROR,
        error: e
      }));
      continue;
    }

    if (!updateRequest) {
      // Propagate
      newMessages.push(currentInflight);
      continue;
    }

    storage.emit("messageMatched", _message, sourcePath);

    if (updateRequest !== 1) {
      var _updateRequest = updateRequest,
          data = _updateRequest.data,
          messages = _updateRequest.messages;
      instance._data = data;
      storage.emit("stateNewData", data, sourcePath, _message);
      instance.emit("stateNewData", data, sourcePath, _message);

      for (var _i6 = 0; _i6 < messages.length; _i6++) {
        var m = messages[_i6];
        newMessages.push(createInflightMessage(storage, sourcePath, m));
      }
    }
  }

  return newMessages;
}
function processEffects(storage, inflightMsgs) {
  var effects = [];

  outer: for (var _i8 = 0; _i8 < inflightMsgs.length; _i8++) {
    var _ref2 = inflightMsgs[_i8];
    var _message = _ref2._message;
    var _source = _ref2._source;

    for (var _i10 = 0, _storage$_effects2 = storage._effects; _i10 < _storage$_effects2.length; _i10++) {
      var effect = _storage$_effects2[_i10];

      if (!isMatchingSubscription(effect.subscribe, _message)) {
        continue;
      }

      storage.emit("messageMatched", _message, []);
      effects.push(runEffect(storage, effect, _message, _source));
      continue outer;
    }

    storage.emit("unhandledMessage", _message, _source);

    if (storage.listeners("unhandledMessage").length === 0) {
      logUnhandledMessage(_message, _source);
    }
  }

  return Promise.all(effects).then(function () {
    return undefined;
  });
}
/**
 * Runs the supplied effect and attempts to collect any reply-message from it,
 * rejections will cause an `EffectErrorMessage` to be sent as a reply.
 *
 * The resulting promise will always succeed.
 */

function runEffect(storage, _ref3, message, source) {
  var effect = _ref3.effect,
      name = _ref3.name;

  var onSuccess = function onSuccess(msg) {
    return msg ? storage.replyMessage(msg, source, name) : null;
  };

  var onError = function onError(e) {
    return storage.replyMessage({
      tag: EFFECT_ERROR,
      cause: message,
      error: e
    }, source, name);
  };

  var promise = new Promise(function (resolve) {
    resolve(effect(message, source));
  }).then(onSuccess, onError);

  var dropPromise = function dropPromise() {
    storage._running["delete"](promise);

    if (storage._running.size === 0) {
      for (var _i12 = 0, _storage$_waiting2 = storage._waiting; _i12 < _storage$_waiting2.length; _i12++) {
        var r = _storage$_waiting2[_i12];
        r();
      }

      storage._waiting = [];
    }
  };

  storage._running.set(promise, {
    name: name,
    message: message,
    source: source
  });

  return promise.then(dropPromise, dropPromise);
}
function createSnapshot(node) {
  return Object.keys(node._nested).reduce(function (a, key) {
    var nested = node._nested[key];
    a[key] = {
      id: nested._id,
      // We assume it is immutably updated
      data: nested._data,
      nested: createSnapshot(nested)
    };
    return a;
  }, {});
}
function logUnhandledMessage(msg, path) {
  var source = "from [" + path.join(", ") + "].";

  switch (msg.tag) {
    case EFFECT_ERROR:
      console.error("Unhandled effect error:", msg.error, source);
      break;

    case UPDATE_ERROR:
      console.error("Unhandled update error:", msg.error, source);
      break;

    default:
      console.error("Unhandled message:", msg, source);
  }
}

export { State, Storage, createInflightMessage, createSnapshot, ensureModel, findState, getModelById, logUnhandledMessage, processEffects, processInstanceMessages, processMessages, _restoreSnapshot as restoreSnapshot, runEffect, tryAddModel };
//# sourceMappingURL=storage.js.map
